import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';
import './CRMDashboard.css';

interface NotificationsMenuProps {
  userId: string;
}

const ACTION_ICONS: Record<string, string> = {
  admin_instruct: '📢',
  staff_update: '💬',
  lead_assigned: '👤',
  status_change: '🔄',
  note: '📝',
  alert: '⚠️'
};

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({ userId }) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const prevCountRef = useRef(0);

  const playSoundNotification = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const audioCtx = new AudioCtx();
      
      // Play a dual tone chord (friendly notification sound)
      const playTone = (freq: number, startDelay: number, duration: number) => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, audioCtx.currentTime + startDelay);
        
        gain.gain.setValueAtTime(0, audioCtx.currentTime + startDelay);
        gain.gain.linearRampToValueAtTime(0.12, audioCtx.currentTime + startDelay + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + startDelay + duration);
        
        osc.start(audioCtx.currentTime + startDelay);
        osc.stop(audioCtx.currentTime + startDelay + duration);
      };

      // Synthesize a pleasant "ping" chord
      playTone(523.25, 0, 0.4);      // C5
      playTone(659.25, 0.08, 0.35);   // E5
      playTone(783.99, 0.16, 0.3);    // G5
    } catch (err) {
      console.error('Failed to play notification audio:', err);
    }
  };

  const sendPushNotification = (title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, { body });
      } catch (e) {
        console.error('Failed to trigger native notification:', e);
      }
    }
  };

  const fetchNotifs = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await apiService.getUnreadNotifications(userId);
      if (res.success) setNotifications(res.data);
    } catch (e) { console.error('Error fetching notifications:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    fetchNotifs();
    // Poll every 30 minutes only if active tab to prevent egress exhaustion
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchNotifs();
      }
    }, 1800000);
    return () => clearInterval(interval);
  }, [userId]);

  useEffect(() => {
    const prevCount = prevCountRef.current;
    const currentCount = notifications.length;
    
    if (currentCount > prevCount && prevCount > 0) {
      playSoundNotification();
      const newest = notifications[0];
      if (newest) {
        sendPushNotification(newest.title, newest.message);
      }
    }
    prevCountRef.current = currentCount;
  }, [notifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiService.markNotificationAsRead(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) {}
  };

  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0) return;
    try {
      await apiService.markAllNotificationsAsRead(userId);
      setNotifications([]);
      setIsOpen(false);
    } catch (e) {}
  };

  const handleNotificationClick = async (n: any) => {
    // 1. Mark as read
    await apiService.markNotificationAsRead(n.id);
    setNotifications(prev => prev.filter(item => item.id !== n.id));
    setIsOpen(false);

    // 2. Navigate based on entity
    if (n.entity_type === 'lead') {
      navigate('/crm/app/leads');
    } else if (n.entity_type === 'customer') {
      // In a more advanced implementation, we could try to pop open the CustomerMessages modal directly.
      // For now, redirecting to the workflow page is sufficient.
      navigate('/crm/app/customers');
    }
  };

  const timeAgo = (dateStr: string) => {
    const seconds = Math.floor((new Date().getTime() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="crm-notifications-wrapper" ref={dropdownRef} style={{ position: 'relative' }}>
      <button 
        className="crm-nav-link" 
        onClick={() => setIsOpen(!isOpen)} 
        style={{ border: 'none', cursor: 'pointer', background: 'transparent', position: 'relative' }}
        title="Notifications"
      >
        <span style={{ fontSize: '18px' }}>🔔</span>
        {notifications.length > 0 && (
          <span style={{
            position: 'absolute', top: 4, right: 4, background: 'var(--crm-danger)', color: 'white',
            fontSize: '9px', fontWeight: 800, padding: '2px 5px', borderRadius: '10px',
            transform: 'translate(50%, -50%)', border: '2px solid white'
          }}>
            {notifications.length > 99 ? '99+' : notifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 340,
          background: 'white', border: '1px solid var(--crm-border)', borderRadius: '12px',
          boxShadow: 'var(--crm-shadow-lg)', zIndex: 1000, overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: '400px'
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--crm-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700 }}>Notifications</h4>
            {notifications.length > 0 && (
              <button 
                onClick={handleMarkAllAsRead}
                style={{ background: 'none', border: 'none', color: 'var(--crm-primary)', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && notifications.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--crm-text-muted)', fontSize: '13px' }}>Loading...</div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: 8 }}>📭</div>
                <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)', fontWeight: 500 }}>You're all caught up!</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {notifications.map((n, i) => (
                  <div 
                    key={n.id} 
                    onClick={() => handleNotificationClick(n)}
                    style={{ 
                      padding: '12px 16px', display: 'flex', gap: 12, cursor: 'pointer',
                      borderBottom: i < notifications.length - 1 ? '1px solid var(--crm-border-light)' : 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ fontSize: '18px', marginTop: 2 }}>{ACTION_ICONS[n.entity_type === 'lead' ? 'lead_assigned' : 'admin_instruct'] || '🔔'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--crm-text-primary)', marginBottom: 2 }}>{n.title}</div>
                      <div style={{ fontSize: '12px', color: 'var(--crm-text-secondary)', lineHeight: 1.4, WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', display: '-webkit-box' }}>
                        {n.message}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--crm-primary)', marginTop: 6, fontWeight: 500 }}>
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                    <button 
                      onClick={(e) => handleMarkAsRead(n.id, e)}
                      style={{ background: 'none', border: 'none', color: 'var(--crm-text-muted)', cursor: 'pointer', padding: 4, height: 'fit-content' }}
                      title="Mark as read"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsMenu;
